// shop/delivery.js — доставка СДЭК (клиентская часть)
// =============================================================================
// Никаких тарифов и пунктов выдачи в коде: и то и другое приходит из API СДЭК
// через собственный сервер (см. server/cdek.js). Ключи СДЭК живут только там —
// в браузер они не попадают.
//
//   GET  /api/cdek/cities?q=…           подсказки городов (нужен код города)
//   GET  /api/cdek/points?city_code=…   пункты выдачи именно этого города
//   POST /api/cdek/quote                стоимость и срок

export const METHODS = {
  pickup:         { label: 'Самовывоз из мастерской', needsCity: false, needsPvz: false, needsAddress: false },
  'cdek-pvz':     { label: 'СДЭК — пункт выдачи',     needsCity: true,  needsPvz: true,  needsAddress: false },
  'cdek-courier': { label: 'СДЭК — курьер до двери',  needsCity: true,  needsPvz: false, needsAddress: true  },
};

// Два адреса одного и того же API:
//   /api/cdek/cities        — красивый путь (Netlify, Node, Apache с rewrite)
//   /api/cdek.php?route=…   — прямой вызов PHP
//
// На обычном хостинге красивый путь держится на .htaccess, а он начинается
// с точки: FTP-клиенты и файловые менеджеры часто прячут такие файлы, и он
// просто не доезжает. Тогда первый же запрос получает 404 — переключаемся на
// прямой вызов и дальше ходим по нему.
let usePhpPath = false;

function buildUrl(route, params) {
  const search = new URLSearchParams(params || {});
  if (usePhpPath) {
    search.set('route', route);
    return '/api/cdek.php?' + search.toString();
  }
  const qs = search.toString();
  return '/api' + route + (qs ? '?' + qs : '');
}

async function request(route, { params, method = 'GET', body } = {}) {
  const send = () => fetch(buildUrl(route, params), {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  let res = await send();

  // 404 на красивом пути = нет перезаписи URL. Пробуем PHP-адрес один раз.
  if (res.status === 404 && !usePhpPath) {
    usePhpPath = true;
    res = await send();
  }

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((data && data.error) || ('HTTP ' + res.status));
    err.status = res.status;
    throw err;
  }
  return data;
}

// ── Города ──────────────────────────────────────────────────────────────────
export async function cities(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return [];
  return request('/cdek/cities', { params: { q } });
}

// ── Пункты выдачи ───────────────────────────────────────────────────────────
// Всегда по коду города: пока города нет, списка пунктов тоже нет.
// Улица (street) сужает выдачу до ближайших — в Москве пунктов больше сотни,
// и список без фильтра бесполезен. Ранжирование происходит на нашем сервере
// по уже закэшированному списку, без дополнительных запросов в СДЭК.
export async function pickupPoints(cityCode, { city, street, limit = 5 } = {}) {
  if (!cityCode) return { items: [], basis: 'city', total: 0 };

  const params = { city_code: String(cityCode), limit: String(limit) };
  if (city) params.city = city;
  if (street) params.q = street;

  return request('/cdek/points', { params });
}

// ── Расчёт ──────────────────────────────────────────────────────────────────
// Никакой «примерной» цены: если перевозчик не ответил, честно сообщаем об
// ошибке. Показать выдуманную сумму хуже, чем не показать никакой — покупатель
// увидел бы одну цену, а заплатил другую.
export async function quote({ method, cityCode, weight }) {
  if (method === 'pickup') {
    return { cost: 0, minDays: 0, maxDays: 0, ok: true, pickup: true };
  }
  if (!cityCode) {
    return { ok: false, reason: 'no-city' };
  }

  try {
    const data = await request('/cdek/quote', {
      method: 'POST',
      body: { toCode: cityCode, method, weight },
    });
    return {
      ok: true,
      cost: data.cost,
      minDays: data.minDays,
      maxDays: data.maxDays,
      test: Boolean(data.test),
    };
  } catch (e) {
    return { ok: false, reason: 'carrier', message: e.message };
  }
}

// ── Даты ────────────────────────────────────────────────────────────────────
// Диапазон приходит от СДЭК (calendar_min/calendar_max) — считаем от него,
// а не от собственных догадок. Воскресенья пропускаем: ПВЗ чаще закрыты.
export function deliveryDates(minDays, maxDays) {
  const start = new Date();
  const first = Math.max(Number(minDays) || 0, 1);
  const span = Math.max((Number(maxDays) || first) - first, 0);
  const dates = [];

  for (let i = 0; dates.length < span + 4 && i < span + 20; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + first + i);
    if (d.getDay() === 0) continue;
    dates.push(d);
  }
  return dates.slice(0, 6);
}

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

export function formatDate(date) {
  return date.getDate() + ' ' + MONTHS[date.getMonth()] + ', ' + WEEKDAYS[date.getDay()];
}

export function isoDate(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return date.getFullYear() + '-' + m + '-' + d;
}
